import type { RefinementCtx } from "zod"

import { z as zod } from "zod"
import { allowedCountryCodesForPhone } from "@/src/utils/ui-utils"

export const addZodError = (ctx: RefinementCtx, path: string, message: string) => {
    ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message,
        path: [path],
    })
}

export type FormSchemaClientType = zod.infer<typeof FormSchemaClient>

export type FormSchemaBulkUpload = zod.infer<typeof BulkUploadFormSchema>
export type FormSchemaApplyJob = zod.infer<typeof ApplyJobFormSchema>
export type FormSchemaAddUser = zod.infer<typeof AddUserSchema>
export type FormSchemaRolePermission = zod.infer<typeof RolePermissionSchema>
export type FormAutoSourceJobSchema = zod.infer<typeof AutoSourceJobSchema>
export type FormSchemaWorkspaceType = zod.infer<typeof FormSchemaWorkspace>

export const AutoSourceJobSchema = zod.object({
    jobId: zod
        .string()
        .nullable()
        .refine((val) => val !== null, {
            message: "Job ID is required",
        }),
    mandateId: zod
        .string()
        .nullable()
        .refine((val) => val !== null, {
            message: "Mandate ID is required",
        }),
})

export const FormSchema = zod.object({
    leadId: zod
        .object({
            label: zod.any(),
            value: zod.string().min(0, { message: "Candidate is required!" }),
        })
        .nullable()
        .refine((val) => val !== null, {
            message: "Candidate is required!",
        }),
    interviewMode: zod.string(),
    interviewRound: zod.string(),
    interviewDate: zod.any(),
})

// Selection
export const FormSchemaSelection = zod.object({
    leadId: zod
        .object({
            label: zod.any(),
            value: zod.string().min(0, { message: "Candidate is required!" }),
        })
        .nullable()
        .refine((val) => val !== null, {
            message: "Candidate is required!",
        }),
    selectionDate: zod.any(),
    selectionProof: zod.string(),
})

// Client
export const FormSchemaClient = zod.object({
    name: zod.string().min(1, { message: "Client name is required" }),
    websiteLink: zod.string().optional(),
    aboutClient: zod.string().min(1, { message: "About Client is required" }),
    email: zod
        .string()
        .min(1, { message: "Email is required" })
        .email({ message: "Email must be a valid email address" }),
    logoUrl: zod.string().optional(),
})

export const BulkUploadFormSchema = zod.object({
    jobId: zod
        .object({
            name: zod.string(),
            id: zod.string().min(0, { message: "Job is required" }),
        })
        .nullable()
        .refine((val) => val !== null, {
            message: "Job is required",
        }),
    csv: zod.any(),
    duplicate: zod.any(),
})

export const ApplyJobFormSchema = zod.object({
    name: zod.string().min(1, { message: "Application name is required" }),
    phoneNumber: zod
        .string()
        .regex(allowedCountryCodesForPhone, { message: "Phone number must be 10 digits" })
        .min(1, { message: "Phone number is required" }),
    email: zod
        .string()
        .min(1, { message: "Email is required" })
        .email({ message: "Email must be a valid email address!" }),
    city: zod.string().min(1, { message: "City is required!" }),
    mandateId: zod.any(),
    jobTitle: zod.any(),
    currentCompany: zod.any(),
    dateOfBirth: zod.any(),
    gender: zod.any(),
    languages: zod.any(),
    currentCtcInLakhs: zod.any(),
    expectedCtcInLakhs: zod.any(),
    noticePeriod: zod.any(),
    skills: zod.any(),
    college: zod.any(),
    degree: zod.any(),
    educationLevel: zod.any(),
    experienceInYears: zod.any(),
    resumeLink: zod.any(),
    area: zod.any(),
    preferredCity: zod.any(),
})

export const AddUserSchema = zod.object({
    fullName: zod.string().min(1, { message: "Applicant name is required" }),
    email: zod
        .string()
        .min(1, { message: "Email is required" })
        .email({ message: "Email must be a valid email address" }),
    phoneNumber: zod
        .string()
        .regex(allowedCountryCodesForPhone, { message: "Phone number must be 10 digits" })
        .min(1, { message: "Phone number is required" }),
    roles: zod.any(),
    managerId: zod.any(),
    isAiScreeningEnabled: zod.boolean(),
})

// permission
export const RolePermissionSchema = zod.object({
    admin: zod.any(),
    manager: zod.any(),
    recruiter: zod.any(),
})

// Workspace
export const FormSchemaWorkspace = zod.object({
    name: zod.string().min(1, { message: "Workspace name is required" }),
    logoUrl: zod.string().optional(),
    websiteUrl: zod.union([
        zod.string().url({ message: "Must be a valid URL" }),
        zod.literal(""),
    ]).optional(),
    aboutWorkspace: zod.string().min(1, { message: "About workspace is required" }),
    config: zod.object({
        maxJobLimit: zod.number().min(1, { message: "Max job limit must be at least 1" }),
        allowGuestUsers: zod.boolean(),
        preferredLanguages: zod.array(zod.string()).min(1, { message: "At least one preferred language is required" }),
    }),
    roundTypeCredits: zod.object({
        QUICK_SCREENING: zod.number().min(0, { message: "Credits must be 0 or greater" }),
        DETAILED_ASSESSMENT: zod.number().min(0, { message: "Credits must be 0 or greater" }),
        TECH_ASSESSMENT: zod.number().min(0, { message: "Credits must be 0 or greater" }),
        TECHNICAL_INTERVIEW: zod.number().min(0, { message: "Credits must be 0 or greater" }),
        MANAGERIAL_INTERVIEW: zod.number().min(0, { message: "Credits must be 0 or greater" }),
        BEHAVIORAL_ASSESSMENT: zod.number().min(0, { message: "Credits must be 0 or greater" }),
        HUMAN_ROUND: zod.number().min(0, { message: "Credits must be 0 or greater" }),
    }),
    userIds: zod.array(zod.string()).min(1, { message: "At least one user is required" }),
})
